import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { partidoService } from '@/services/PartidoService';
import { Partido, PartidoFilters } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

interface UsePartidosEntrenadorResult {
  partidos: Partido[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setFiltros: (filtros: PartidoFilters) => void;
}

/**
 * Hook para gestionar los partidos asignados a un entrenador
 * Optimizado con React Query para cacheo y revalidación inteligente
 * @param filtros - Filtros opcionales para los partidos
 * @returns Objeto con partidos, estado de carga y funciones
 */
export const usePartidosEntrenador = (filtros?: PartidoFilters): UsePartidosEntrenadorResult => {
  const { user } = useAuth();
  const [filtrosActuales, setFiltrosActuales] = useState<PartidoFilters>(filtros || {});

  const {
    data: partidos = [],
    isLoading: loading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['partidos-entrenador', user?.id, filtrosActuales],
    queryFn: async () => {
      if (!user?.id) throw new Error('Usuario no autenticado');
      
      const response = await partidoService.getPartidosEntrenadorOptimizado(user.id, filtrosActuales);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Error al obtener partidos');
      }
    },
    enabled: !!user?.id, // Solo ejecutar si hay usuario
    staleTime: 1000 * 60 * 10, // Datos frescos por 10 minutos
    retry: 1
  });

  /**
   * Actualiza los filtros
   * Al cambiar el estado, useQuery detectará el cambio en queryKey y recargará si es necesario
   */
  const setFiltros = (nuevosFiltros: PartidoFilters) => {
    setFiltrosActuales(nuevosFiltros);
  };

  /**
   * Wrapper para refetch compatible
   */
  const handleRefetch = async () => {
    await refetch();
  };

  return {
    partidos,
    loading,
    error: queryError instanceof Error ? queryError.message : null,
    refetch: handleRefetch,
    setFiltros
  };
};
